import * as React from 'react'

export interface IStep {
  id: string
  label: string
}

interface IProps {
  steps: IStep[]
  currentStepId: string
  completedStepIds: string[]
  // Optional so the accordion can render as a pure indicator when back-
  // navigation isn't wanted.
  onStepSelect?: (stepId: string) => void
  // Only ever called for the current step, whose body expands inline.
  renderBody: (step: IStep) => React.ReactNode
}

type StepState = 'complete' | 'current' | 'upcoming'

function stepState(isCurrent: boolean, isComplete: boolean): StepState {
  if (isCurrent) { return 'current' }
  if (isComplete) { return 'complete' }
  return 'upcoming'
}

// Spoken status so screen-reader users hear progress, not just the label. Color
// and the filled marker convey this visually; this is the aural channel.
const STATE_LABELS: Record<StepState, string> = {
  complete: 'completed',
  current: 'current step',
  upcoming: 'upcoming',
}

// Completed steps render as buttons so the host can let the user jump back; the
// current and upcoming steps render as non-interactive headers.
export function StepAccordion({
  steps,
  currentStepId,
  completedStepIds,
  onStepSelect,
  renderBody,
}: IProps) {
  return (
    // Explicit role="list" survives the `list-style: none` that some screen
    // readers use to drop list semantics (Safari/VoiceOver).
    <ol className="trial-accordion" role="list">
      {steps.map((step, index) => {
        const isCurrent = step.id === currentStepId
        const isComplete = completedStepIds.includes(step.id)
        const state = stepState(isCurrent, isComplete)
        // Only completed steps (and not the one you're on) are navigable.
        const isSelectable = isComplete && !isCurrent

        const headerInner = (
          <>
            <span className="trial-accordion__marker">{index + 1}</span>
            <span className="trial-accordion__label">{step.label}</span>
            <span className="trial-accordion__status">{STATE_LABELS[state]}</span>
          </>
        )

        return (
          <li
            key={step.id}
            className={`trial-accordion__step trial-accordion__step--${state}`}
            data-testid={`trial-step-${step.id}`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            {isSelectable ? (
              <button
                type="button"
                className="trial-accordion__header"
                aria-expanded={false}
                onClick={() => onStepSelect?.(step.id)}
              >
                {headerInner}
              </button>
            ) : (
              <div className="trial-accordion__header">{headerInner}</div>
            )}
            {isCurrent && (
              <div className="trial-accordion__body" data-testid={`trial-step-body-${step.id}`}>
                {renderBody(step)}
              </div>
            )}
          </li>
        )
      })}
    </ol>
  )
}
